import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "../../lib/utils"

/**
 * El botón de Aturno. Un solo lugar donde vive la forma y el color de un botón.
 *
 * Forma: pastilla (`rounded-full`) en todos los tamaños. Antes la base era
 * `rounded-xl` y los CTA de la landing eran `rounded-full` escritos a mano, así
 * que la misma app tenía dos redondeados según la pantalla. Se eligió la
 * pastilla porque es la que ya usaba la landing y funciona igual en un CTA
 * grande y en un botón chico.
 *
 * Color: sale de los tokens `--primary` y `--ring` (definidos en index.css y
 * reescritos por `aplicarBranding` con los colores del negocio, tanto en su
 * página pública como en el panel). Nunca un hex acá: si aparece un `bg-[#...]`
 * en un botón, es un botón que se salió del sistema.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // El blur y la transparencia son para que se lea sobre el fondo animado
        // de la landing sin tapar el degradado.
        outline:
          "border border-input bg-background/90 backdrop-blur hover:bg-accent hover:text-accent-foreground",
        // Los dos de abajo son para un botón parado sobre un fondo del color de
        // la marca (el cierre naranja del footer): ahí `default` es naranja
        // sobre naranja, o sea invisible. Se invierten los mismos dos tokens en
        // vez de escribir un blanco a mano, así que si cambia el color de la
        // marca siguen funcionando.
        inverso:
          "bg-primary-foreground text-primary shadow-sm hover:bg-primary-foreground/90",
        inversoOutline:
          "border-2 border-primary-foreground/60 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-9 px-4",
        lg: "h-11 px-10",
        // Los CTA de la landing: más alto y con más aire que un botón de formulario.
        cta: "h-14 px-8 text-base font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
